/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { arrayByLength, uniq } from "../../../../../shared";
import { last } from "lodash";

const CATEGORIES_AXIS = "calendar.default";
const SERIES_AXIS = "attributes.default";
const [YEAR_FREQUENCY, MONTH_FREQUENCY, WEEK_FREQUENCY] = [1, 4, 7];
const [ATTR_PRICE, ATTR_ENDING_STOCKS] = [60, 5];

export function useHighchartTransformer(olapData: OLAPTransformerResult | null): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const [calendarParameter] = useParameter<number[]>("@calendar");
  const [calendarItems] = useDictionary(dictionaryService, "calendar-origin");
  const olapColumns = olapData?.columns || [];

  const selectedMonthByYear = calendarParameter?.reduce((records, calendarId) => {
    const selection = String(calendarId);
    const key = selection.slice(1, 5);
    const frequency = +selection[0];
    const year = +`1${key}0101`;
    if (frequency === MONTH_FREQUENCY) {
      records[year] = (records[year] || 0) + 1;
    }
    return records;
  }, {} as Record<number, number>) || {};

  const widthMonthByYear = olapColumns.reduce((records, column) => {
    const selection = String(column.selection[CATEGORIES_AXIS]);
    const key = selection.slice(1, 5);
    const frequency = +selection[0];
    const year = +`1${key}0101`;
    if (frequency === MONTH_FREQUENCY) {
      records[year] = (records[year] || 0) + 1;
    }
    return records;
  }, {} as Record<number, number>);

  const [yearsCategories, monthesCategories, weeksCategories] = useMemo(() =>
    [YEAR_FREQUENCY, MONTH_FREQUENCY, WEEK_FREQUENCY]
      .map(frequency => {
        const frequencyColumns = olapColumns.filter(colum => {
          const colFrequency = +String(colum.selection[CATEGORIES_AXIS])[0];
          return colFrequency === frequency;
        });
        const frequencyCalendarIds = calendarParameter?.filter(calendarId => {
          const calendarFrequency = Number(String(calendarId)[0]);
          return calendarFrequency === frequency;
        });
        const lastColumn = frequencyColumns[frequencyColumns.length - 1];
        const lastColIndex = frequencyCalendarIds?.indexOf(lastColumn?.selection[CATEGORIES_AXIS] as number) || 0;
        const ignoreCalendarIds = frequencyCalendarIds?.slice(lastColIndex + 1) || [];
        
        return frequencyCalendarIds?.filter(calendarId => !ignoreCalendarIds.includes(calendarId)).map(calendarId => {
          const calendarItem = calendarItems?.find(item => item.id === String(calendarId));
          const name = calendarItem?.getValue<string>("name") || '';
          return {
            calendarId, name
          };
        }) || []
      })
    , [olapColumns, calendarItems]);

  const [priceRow, endingStocksRow] = [ATTR_PRICE, ATTR_ENDING_STOCKS].map(attr =>
    olapData?.rows.find(row => Number(row.selection[SERIES_AXIS]) === attr)
  );

  const options = useChartOptions({
    tooltip: {
      useHTML: true,
      formatter: function () {
        const attr = this.point.series.name;
        const { category, avgYear } = this.point as any;
        const pointValue = Highcharts.numberFormat(this.point.y || 0, 0);
        return `<div>
            <b>${attr}</b><br/>
            <b>${avgYear || category}</b>: <span>${pointValue}</span>
        </div>`;
      },
      outside: true,
      style: {
        zIndex: 100,
      },
    },
  }, [
    chartPipe("variwide"),
    legendPipe("bottom", {
      useHTML: true,
      labelFormatter: function () {
        const { name } = this;
        return name;
      }
    }),
    addXAxisPipe(yearsCategories.map(category => category.name), {
      opposite: true,
      visible: false,
    }),
    addXAxisPipe(weeksCategories.map(category => category.name), {
      opposite: true,
      visible: false,
    }),
    addXAxisPipe(monthesCategories.map(category => category.name), {
      visible: true,
      labels: {
        enabled: true,
        formatter: function () {
          const label = this.axis.defaultLabelFormatter.call(this);
          return label;
        }
      },

    }),
    addYAxisPipe({
      gridLineColor: '#fff',
      title: {
        text: "Price (USD/mt)",
      },
      labels: {
        enabled: true,
        formatter: function () {
          const label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
    }),
    addYAxisPipe({
      opposite: true,
      gridLineColor: '#fff',
      title: {
        text: "Ending Stocks (MMT)",
      },
      labels: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          const label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
    }),
  ]);

  useEffect(() => {
    if (olapData) {
      const priceData = weeksCategories.map(weekCategory =>
        priceRow?.points.find(point => point.column.selection[CATEGORIES_AXIS] === weekCategory.calendarId)
      );

      const priceAVGData = yearsCategories.map(yearCategory =>
        priceRow?.points.find(point => point.column.selection[CATEGORIES_AXIS] === yearCategory.calendarId)
      );

      const endingStocksData = yearsCategories.map(yearCategory =>
        endingStocksRow?.points.find(point => point.column.selection[CATEGORIES_AXIS] === yearCategory.calendarId)
      );

      const priceSerie: SeriesOptionsType = {
        id: 'prise-spline',
        name: 'Prices',
        type: 'spline',
        data: priceData?.map((point, pointIndex) => {
          const value = point?.value || null;
          const yearKey = String(point?.attributes?.[`${CATEGORIES_AXIS}.id`]).slice(1, 5);
          const otherYearPoints = priceData.filter(p => String(p?.attributes?.[`${CATEGORIES_AXIS}.id`]).slice(1, 5) === yearKey);
          const isMax = Math.max(...otherYearPoints!.map(p => p?.value || 0)) === value;
          const isFirstMax = isMax && point === otherYearPoints!.find(p => p?.value === point?.value);
          return { x: pointIndex, y: value, isMax: isFirstMax };
        }) || [],
        yAxis: 0,
        xAxis: 1,
        color: '#B40000',
        zIndex: 3,
        marker: {
          radius: 0,
        },
        showInLegend: true,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        dataLabels: {
          enabled: true,
          useHTML: true,
          allowOverlap: true,
          y: -4,
          formatter: function () {
            const isMax = this.point['isMax'];
            return isMax ? `${Highcharts.numberFormat(this.point.y!, 0)} <span style='font-size: 10px'>(max)</span>` : '';
          }
        },
        turboThreshold: 10000,
      };

      const priceAVGSerie: SeriesOptionsType = {
        id: 'prise-avg',
        name: '&Oslash; Average Price of the year',
        type: 'spline',
        data: priceData?.map((point, x) => {
          const calendarId = String(point?.attributes?.[`${CATEGORIES_AXIS}.id`]);
          const value = point?.value || 0;
          const yearKey = calendarId.slice(1, 5);
          const otherYearPoints = priceData.filter(p => String(p?.attributes?.[`${CATEGORIES_AXIS}.id`]).slice(1, 5) === yearKey);
          const isMax = Math.max(...otherYearPoints!.map(p => p?.value || 0)) === value;
          const lastMinPoint = isMax ? otherYearPoints.reverse().find(p => p?.value === value) : null;
          const showAVG = isMax && lastMinPoint?.column.selection[CATEGORIES_AXIS] === point?.column.selection[CATEGORIES_AXIS];
          const avgPoint = priceAVGData?.find(p => String(p?.attributes?.[`${CATEGORIES_AXIS}.id`]).slice(1, 5) === yearKey);
          const avgYear = avgPoint?.attributes && avgPoint?.attributes?.[`${CATEGORIES_AXIS}.name`];
          const y = avgPoint?.value || null;
          return { x, y, showAVG, avgYear };
        }) || [],
        yAxis: 0,
        xAxis: 1,
        color: 'black',
        zIndex: 2,
        marker: {
          radius: 0,
        },
        showInLegend: true,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        dataLabels: {
          enabled: true,
          useHTML: true,
          allowOverlap: true,
          y: 28,
          formatter: function () {
            const showAVG = this.point['showAVG'];
            return showAVG ? `<span style='font-size: 10px'><span style='font-size: 9px'>&Oslash;</span> ${Highcharts.numberFormat(this.point.y!, 0)}</span>` : '';
          }
        },
        turboThreshold: 10000,
      };

      const endingStocksSerie: SeriesOptionsType = {
        id: 'ending-stocks',
        name: 'Ending Stocks',
        type: 'variwide',
        data: endingStocksData?.map(point => {
          const yearKey = Number(point?.attributes?.[`${CATEGORIES_AXIS}.id`]);
          const width = widthMonthByYear[yearKey] || selectedMonthByYear[yearKey];
          return [
            point?.attributes?.[`${CATEGORIES_AXIS}.name`] || '',
            point?.value || 0,
            width || 0
          ];
        }) || [],
        yAxis: 1,
        xAxis: 0,
        zIndex: 1,
        showInLegend: true,
        color: '#0F8AC0',
        turboThreshold: 10000,
      };

      const series: SeriesOptionsType[] = [priceSerie, priceAVGSerie, endingStocksSerie];

      const calendarSerieData = monthesCategories.map(category => null);

      const calendarSerie: SeriesOptionsType = {
        type: 'spline',
        id: 'calendar-serie',
        yAxis: 1,
        xAxis: 2,
        color: '#f6f6f6',
        showInLegend: false,
        zIndex: 0,
        data: calendarSerieData,
        turboThreshold: 10000,
      };

      const result: Highcharts.Options = {
        ...options,
        series: series.concat([calendarSerie]),
      };

      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, olapColumns, yearsCategories, monthesCategories, weeksCategories]);
  return state;
}
